// The cursor stack behind a server-paginated table.
//
// A cursor is only valid for the request that issued it: `search_paging.rs`
// fingerprints the predicate into the token (`fingerprint`) and its
// continuation check answers a mismatched one with a 400. So the stack is
// keyed by the request it belongs to and thrown away the moment that
// request changes — during render, before the query can subscribe with a
// stale one.
//
// kore ships `useKoreCursorPagination` for this, and it is the wrong shape
// for us twice over:
//
//   * Its reset runs in a `useEffect`, which lands one commit after the query
//     has already subscribed with the new predicate and the old token. Exactly
//     one 400 escapes before the reset takes hold.
//   * Its documented `resetOn` value is `search.rq` from `useKoreSearch` — but
//     a page that renders its own search input never passes `query` into that
//     hook (kore's SearchBar would otherwise render a second, differently
//     styled field), so `search.rq` does not change on a keystroke at all.
//     Nothing about a free-text change would reset the stack.

import { useState } from "react";

export interface UseCursorStackOptions {
    /**
     * Everything about the request except the cursor: filters, free text,
     * sort, page size. Compared by value (JSON), not by reference, so callers
     * need not memoise — `usePageSearch`'s `predicate` hands back a fresh
     * object every render. Whatever is left out of this object cannot reset
     * the stack, so page size belongs in it: changed page boundaries
     * invalidate a cursor as surely as a changed predicate does.
     */
    request: unknown;
}

/** The footer's affordances, once the response for `pageToken` is in hand. */
export interface CursorPager {
    /** 1-indexed, for the footer's readout. */
    page: number;
    hasPrev: boolean;
    hasNext: boolean;
    onFirstPage: () => void;
    onPrevPage: () => void;
    onNextPage: () => void;
}

export interface CursorStack {
    /** The cursor to send with the request being built now. `null` on page one. */
    pageToken: string | null;
    /**
     * Pager state, given the `nextPageToken` the current response carried.
     *
     * A second step rather than an option, because the response cannot exist
     * until the request carrying `pageToken` has been sent: this hook runs
     * before the query, `pager` after it. Folding the response into the hook's
     * arguments is what forces the effect-based reset kore ended up with.
     */
    pager: (nextPageToken: string | null) => CursorPager;
}

export function useCursorStack({ request }: UseCursorStackOptions): CursorStack {
    const requestKey = JSON.stringify(request);
    const [stack, setStack] = useState<{ key: string; tokens: (string | null)[] }>({
        key: requestKey,
        tokens: [null],
    });

    // Reset during render, not from an effect. React re-runs this component
    // immediately on a render-phase set, but the *current* pass still holds
    // the old state — hence reading through `tokens` below rather than
    // `stack.tokens`, so nothing downstream can build a request around a
    // cursor that has just been invalidated.
    if (stack.key !== requestKey) setStack({ key: requestKey, tokens: [null] });
    const tokens = stack.key === requestKey ? stack.tokens : [null];
    const pageToken = tokens[tokens.length - 1] ?? null;

    return {
        pageToken,
        pager: (nextPageToken) => ({
            page: tokens.length,
            hasPrev: tokens.length > 1,
            // Comparing against the token already sent, rather than gating on
            // `isFetching`, is what keeps Next available during a background
            // refetch of the page already on screen — only a genuinely stale
            // `nextPageToken` (one still describing the previous page) is
            // guarded against here.
            hasNext: nextPageToken !== null && nextPageToken !== pageToken,
            onFirstPage: () => setStack({ key: requestKey, tokens: [null] }),
            onPrevPage: () =>
                setStack((prev) => (prev.tokens.length <= 1 ? prev : { ...prev, tokens: prev.tokens.slice(0, -1) })),
            onNextPage: () => {
                // The same guard as `hasNext`, restated: a disabled button is
                // the affordance, not the invariant.
                if (nextPageToken === null || nextPageToken === pageToken) return;
                setStack((prev) => ({ ...prev, tokens: [...prev.tokens, nextPageToken] }));
            },
        }),
    };
}
